/**
 * Construction Measurement & Bill-Support Packs — Common Types & Validators
 * R39 B6.a + B6.b (plan docs/plans/sector-operations.md row B6 :66, :765-767 and SO-D15 :316).
 *
 * HARD RULE (SO-D15): this feature never calculates or stores money. No rate,
 * amount, value, price, tax, GST, TDS, cess, retention, advance or recovery field
 * anywhere: not in the data, the validators, the CSV columns or the UI.
 * Every validator rejects an input that carries any such key.
 */

// ---------------------------------------------------------------------------
// SO-D15 Money Key Ban & Guard
// ---------------------------------------------------------------------------

export const BANNED_MONEY_KEYS = [
  'rate',
  'amount',
  'value',
  'price',
  'tax',
  'gst',
  'tds',
  'cess',
  'retention',
  'advance',
  'recovery',
  'payable',
  'certified',
  'billamount',
  'unitprice',
  'unitrate',
  'totalprice',
  'totalamount',
] as const

/**
 * Recursively inspects an object or array to ensure no money-related keys exist.
 * Rejects with an error if any banned key (case-insensitive, normalized) is found.
 */
export function assertNoMoneyKeys(input: unknown, path = ''): void {
  if (!input || typeof input !== 'object') {
    return
  }

  if (Array.isArray(input)) {
    for (let i = 0; i < input.length; i++) {
      assertNoMoneyKeys(input[i], `${path}[${i}]`)
    }
    return
  }

  const record = input as Record<string, unknown>
  for (const key of Object.keys(record)) {
    const normalizedKey = key.toLowerCase().replace(/[^a-z]/g, '')
    for (const banned of BANNED_MONEY_KEYS) {
      if (normalizedKey.includes(banned)) {
        const fullPath = path ? `${path}.${key}` : key
        throw new Error(
          `SO-D15 violation: money field "${fullPath}" is strictly prohibited in construction measurement.`,
        )
      }
    }
    assertNoMoneyKeys(record[key], path ? `${path}.${key}` : key)
  }
}

// ---------------------------------------------------------------------------
// B6.a1 Parties Types & Constants
// ---------------------------------------------------------------------------

export type ConPartyType = 'SUBCONTRACTOR' | 'SUPPLIER' | 'IN_HOUSE_GANG'

export const CON_PARTY_TYPES: readonly ConPartyType[] = [
  'SUBCONTRACTOR',
  'SUPPLIER',
  'IN_HOUSE_GANG',
] as const

export interface ConParty {
  id: string
  workspaceId: string
  name: string
  type: ConPartyType
  trade: string
  externalRef?: string
  projectIds: string[]
  archived: boolean
  createdBy: string
  createdAt: number
  updatedAt: number
  updatedBy?: string
}

export interface UpsertConPartyInput {
  workspaceId: string
  partyId?: string
  name: string
  type: ConPartyType
  trade: string
  externalRef?: string
  projectIds: string[]
  archived?: boolean
  opId?: string
}

export interface ListConPartiesInput {
  workspaceId: string
  projectId?: string
  includeArchived?: boolean
  limit?: number
}

// ---------------------------------------------------------------------------
// B6.a2 Measurement Lines Types & Constants
// ---------------------------------------------------------------------------

export const STANDARD_MEASUREMENT_UNITS: readonly string[] = [
  'm',
  'm2',
  'm3',
  'kg',
  't',
  'nos',
  'rmt',
  'lot',
] as const

export type StandardMeasurementUnit = (typeof STANDARD_MEASUREMENT_UNITS)[number]

export type ConMeasurementState = 'DRAFT' | 'SUBMITTED' | 'ACCEPTED' | 'DISPUTED' | 'CANCELLED'

export const CON_MEASUREMENT_STATES: readonly ConMeasurementState[] = [
  'DRAFT',
  'SUBMITTED',
  'ACCEPTED',
  'DISPUTED',
  'CANCELLED',
] as const

export interface ConInspectionRef {
  type: 'POUR_CARD' | 'INSPECTION'
  id: string
}

export interface ConSourceLink {
  kind: 'DPR' | 'ISSUE' | 'DRAWING'
  id: string
}

export interface ConMeasurementRevision {
  revision: number
  oldValues: Record<string, unknown>
  newValues: Record<string, unknown>
  editedBy: string
  editedAt: number
}

export interface ConMeasurementCorrection {
  correctionNumber: number
  originalAcceptedQty: number
  newAcceptedQty: number
  reason: string
  authorUid: string
  authorName?: string
  createdAt: number
}

export interface ConMeasurement {
  id: string
  workspaceId: string
  projectId: string
  siteId: string
  partyId: string
  partyName: string
  placeId: string
  workOrderRef: string
  itemRef: string
  itemDescription?: string
  unit: string
  measuredDay: string
  periodLabel?: string
  measuredQty: number
  acceptedQty?: number | null
  cumulativeAcceptedQty?: number
  photoPaths: string[]
  inspectionRefs: ConInspectionRef[]
  sourceLinks: ConSourceLink[]
  state: ConMeasurementState
  submittedBy?: string
  submittedAt?: number
  decidedBy?: string
  decidedAt?: number
  disputeReason?: string
  selfAcceptReason?: string
  revision: number
  kindKey: string // MEASURE:<partyId>
  createdBy: string
  createdAt: number
  updatedAt: number
  updatedBy?: string
}

// ---------------------------------------------------------------------------
// B6.a3 Cumulative Totals Types
// ---------------------------------------------------------------------------

export interface ConMeasureTotal {
  id: string
  workspaceId: string
  projectId: string
  partyId: string
  workOrderRef: string
  itemRef: string
  unit: string
  measuredTotal: number
  acceptedTotal: number
  measuredTotalMilli?: number
  acceptedTotalMilli?: number
  disputedCount: number
  lineCount: number
  lastDay: string
  updatedAt: number
}

// ---------------------------------------------------------------------------
// Callables Input Types
// ---------------------------------------------------------------------------

export interface SaveConMeasurementInput {
  workspaceId: string
  projectId: string
  measurementId?: string
  partyId: string
  placeId: string
  workOrderRef: string
  itemRef: string
  itemDescription?: string
  unit: string
  measuredDay: string
  periodLabel?: string
  measuredQty: number
  photoPaths?: string[]
  inspectionRefs?: ConInspectionRef[]
  sourceLinks?: ConSourceLink[]
  opId?: string
}

export interface SubmitConMeasurementInput {
  workspaceId: string
  projectId: string
  measurementId: string
  opId?: string
}

export interface AcceptConMeasurementInput {
  workspaceId: string
  projectId: string
  measurementId: string
  acceptedQty: number
  note?: string
  selfAcceptReason?: string
  opId?: string
}

export interface DisputeConMeasurementInput {
  workspaceId: string
  projectId: string
  measurementId: string
  reason: string
  selfAcceptReason?: string
  opId?: string
}

export interface CorrectConMeasurementInput {
  workspaceId: string
  projectId: string
  measurementId: string
  newAcceptedQty: number
  reason: string
  selfAcceptReason?: string
  opId?: string
}

export interface CancelConMeasurementInput {
  workspaceId: string
  projectId: string
  measurementId: string
  reason: string
  opId?: string
}

export interface GetConMeasurementInput {
  workspaceId: string
  projectId: string
  measurementId: string
}

export interface ListConMeasurementsInput {
  workspaceId: string
  projectId: string
  partyId?: string
  state?: ConMeasurementState
  fromDay?: string
  toDay?: string
  limit?: number
}

export interface ListDisputedConMeasurementsInput {
  workspaceId: string
  projectId: string
  limit?: number
}

export interface GetConMeasureTotalsInput {
  workspaceId: string
  projectId: string
  partyId?: string
  workOrderRef?: string
  limit?: number
}

// ---------------------------------------------------------------------------
// Fixed-Point Mathematics (Thousandths integers)
// ---------------------------------------------------------------------------

export function toThousandths(val: number): number {
  return Math.round(val * 1000)
}

export function fromThousandths(thousandths: number): number {
  return Math.round(thousandths) / 1000
}

/**
 * Creates a deterministic, safe document ID for cumulative totals based on:
 * projectId, partyId, workOrderRef, itemRef, unit.
 */
export function buildMeasureTotalDocId(
  projectId: string,
  partyId: string,
  workOrderRef: string,
  itemRef: string,
  unit: string,
): string {
  const rawKey = `${projectId}__${partyId}__${workOrderRef.trim()}__${itemRef.trim()}__${unit.trim()}`
  // Simple deterministic djb2 / fnv-like hex digest to avoid ultra-long/illegal characters in Firestore keys
  let h1 = 0xdeadbeef
  let h2 = 0x41c6ce57
  for (let i = 0; i < rawKey.length; i++) {
    const ch = rawKey.charCodeAt(i)
    h1 = Math.imul(h1 ^ ch, 2654435761)
    h2 = Math.imul(h2 ^ ch, 1597334677)
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909)
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909)
  const hex = (h2 >>> 0).toString(16).padStart(8, '0') + (h1 >>> 0).toString(16).padStart(8, '0')
  const safeProject = projectId.replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 20)
  return `tot_${safeProject}_${hex}`
}

// ---------------------------------------------------------------------------
// Validators
// ---------------------------------------------------------------------------

export function validateConPartyName(name: unknown): string {
  if (typeof name !== 'string') {
    throw new Error('Party name must be a string.')
  }
  const trimmed = name.trim()
  if (trimmed.length < 2 || trimmed.length > 120) {
    throw new Error('Party name must be between 2 and 120 characters.')
  }
  return trimmed
}

export function validateConPartyType(type: unknown): ConPartyType {
  if (typeof type !== 'string' || !CON_PARTY_TYPES.includes(type as ConPartyType)) {
    throw new Error('Party type must be one of SUBCONTRACTOR, SUPPLIER, IN_HOUSE_GANG.')
  }
  return type as ConPartyType
}

export function validateConPartyTrade(trade: unknown): string {
  if (typeof trade !== 'string') {
    throw new Error('Party trade must be a string.')
  }
  const trimmed = trade.trim()
  if (!trimmed || trimmed.length > 60) {
    throw new Error('Party trade must be between 1 and 60 characters.')
  }
  return trimmed
}

export function validateConPartyExternalRef(ref: unknown): string | undefined {
  if (ref === undefined || ref === null || ref === '') {
    return undefined
  }
  if (typeof ref !== 'string') {
    throw new Error('Party externalRef must be a string if provided.')
  }
  const trimmed = ref.trim()
  if (trimmed.length > 60) {
    throw new Error('Party externalRef must be at most 60 characters.')
  }
  return trimmed || undefined
}

export function validateConPartyProjectIds(ids: unknown): string[] {
  if (!Array.isArray(ids)) {
    throw new Error('Party projectIds must be an array.')
  }
  const clean: string[] = []
  for (const item of ids) {
    if (typeof item !== 'string' || !item.trim()) {
      throw new Error('Each projectId in party projectIds must be a non-empty string.')
    }
    clean.push(item.trim())
  }
  return Array.from(new Set(clean))
}

export function validateWorkOrderRef(ref: unknown): string {
  if (typeof ref !== 'string') {
    throw new Error('workOrderRef must be a string.')
  }
  const trimmed = ref.trim()
  if (trimmed.length < 1 || trimmed.length > 60) {
    throw new Error('workOrderRef must be between 1 and 60 characters.')
  }
  return trimmed
}

export function validateItemRef(ref: unknown): string {
  if (typeof ref !== 'string') {
    throw new Error('itemRef must be a string.')
  }
  const trimmed = ref.trim()
  if (trimmed.length < 1 || trimmed.length > 60) {
    throw new Error('itemRef must be between 1 and 60 characters.')
  }
  return trimmed
}

export function validateItemDescription(desc: unknown): string | undefined {
  if (desc === undefined || desc === null || desc === '') {
    return undefined
  }
  if (typeof desc !== 'string') {
    throw new Error('itemDescription must be a string if provided.')
  }
  const trimmed = desc.trim()
  if (trimmed.length > 300) {
    throw new Error('itemDescription must be at most 300 characters.')
  }
  return trimmed || undefined
}

export function validateMeasurementUnit(unit: unknown): string {
  if (typeof unit !== 'string') {
    throw new Error('unit must be a string.')
  }
  const trimmed = unit.trim()
  if (STANDARD_MEASUREMENT_UNITS.includes(trimmed)) {
    return trimmed
  }
  if (!/^[a-zA-Z]{1,12}$/.test(trimmed)) {
    throw new Error(
      'unit must be one of m, m2, m3, kg, t, nos, rmt, lot, or a custom code of 1–12 letters.',
    )
  }
  return trimmed
}

export function validateMeasuredDay(day: unknown): string {
  if (typeof day !== 'string') {
    throw new Error('measuredDay must be a string.')
  }
  const trimmed = day.trim()
  if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    throw new Error('measuredDay must be in YYYY-MM-DD format.')
  }
  return trimmed
}

export function validatePeriodLabel(label: unknown): string | undefined {
  if (label === undefined || label === null || label === '') {
    return undefined
  }
  if (typeof label !== 'string') {
    throw new Error('periodLabel must be a string if provided.')
  }
  const trimmed = label.trim()
  if (trimmed.length > 40) {
    throw new Error('periodLabel must be at most 40 characters.')
  }
  return trimmed || undefined
}

export function validateMeasurementQuantity(qty: unknown, fieldName = 'measuredQty'): number {
  if (typeof qty !== 'number' || !Number.isFinite(qty)) {
    throw new Error(`${fieldName} must be a valid finite number.`)
  }
  if (qty < 0) {
    throw new Error(`${fieldName} must be greater than or equal to 0.`)
  }
  // At most 3 decimal places
  const scaled = Math.round(qty * 1000)
  if (Math.abs(scaled / 1000 - qty) > 1e-9) {
    throw new Error(`${fieldName} may have at most 3 decimal places.`)
  }
  return Math.round(qty * 1000) / 1000
}

export function validateInspectionRefs(refs: unknown): ConInspectionRef[] {
  if (refs === undefined || refs === null) {
    return []
  }
  if (!Array.isArray(refs)) {
    throw new Error('inspectionRefs must be an array.')
  }
  if (refs.length > 20) {
    throw new Error('inspectionRefs may contain at most 20 entries.')
  }
  const validated: ConInspectionRef[] = []
  for (const item of refs) {
    if (!item || typeof item !== 'object') {
      throw new Error('Each inspectionRef must be an object.')
    }
    const type = (item as { type?: unknown }).type
    const id = (item as { id?: unknown }).id
    if (type !== 'POUR_CARD' && type !== 'INSPECTION') {
      throw new Error('inspectionRef type must be POUR_CARD or INSPECTION.')
    }
    if (typeof id !== 'string' || !id.trim() || id.trim().length > 120) {
      throw new Error('inspectionRef id must be between 1 and 120 characters.')
    }
    validated.push({ type, id: id.trim() })
  }
  return validated
}

export function validateSourceLinks(links: unknown): ConSourceLink[] {
  if (links === undefined || links === null) {
    return []
  }
  if (!Array.isArray(links)) {
    throw new Error('sourceLinks must be an array.')
  }
  if (links.length > 10) {
    throw new Error('sourceLinks may contain at most 10 entries.')
  }
  const validated: ConSourceLink[] = []
  for (const item of links) {
    if (!item || typeof item !== 'object') {
      throw new Error('Each sourceLink must be an object.')
    }
    const kind = (item as { kind?: unknown }).kind
    const id = (item as { id?: unknown }).id
    if (kind !== 'DPR' && kind !== 'ISSUE' && kind !== 'DRAWING') {
      throw new Error('sourceLink kind must be DPR, ISSUE, or DRAWING.')
    }
    if (typeof id !== 'string' || !id.trim() || id.trim().length > 120) {
      throw new Error('sourceLink id must be between 1 and 120 characters.')
    }
    validated.push({ kind, id: id.trim() })
  }
  return validated
}

export function validateDisputeReason(reason: unknown): string {
  if (typeof reason !== 'string') {
    throw new Error('Dispute reason must be a string.')
  }
  const trimmed = reason.trim()
  if (trimmed.length < 3 || trimmed.length > 500) {
    throw new Error('Dispute reason must be between 3 and 500 characters.')
  }
  return trimmed
}

export function validateCorrectionReason(reason: unknown): string {
  if (typeof reason !== 'string') {
    throw new Error('Correction reason must be a string.')
  }
  const trimmed = reason.trim()
  if (trimmed.length < 3 || trimmed.length > 500) {
    throw new Error('Correction reason must be between 3 and 500 characters.')
  }
  return trimmed
}

export function validateCancellationReason(reason: unknown): string {
  if (typeof reason !== 'string') {
    throw new Error('Cancellation reason must be a string.')
  }
  const trimmed = reason.trim()
  if (trimmed.length < 3 || trimmed.length > 500) {
    throw new Error('Cancellation reason must be between 3 and 500 characters.')
  }
  return trimmed
}

export function validateDiscrepancyNote(note: unknown): string {
  if (typeof note !== 'string') {
    throw new Error('Note must be a string.')
  }
  const trimmed = note.trim()
  if (trimmed.length < 3 || trimmed.length > 500) {
    throw new Error('Note must be between 3 and 500 characters.')
  }
  return trimmed
}

// ---------------------------------------------------------------------------
// Combined Input Validators
// ---------------------------------------------------------------------------

export function validateUpsertConPartyInput(input: unknown): UpsertConPartyInput {
  assertNoMoneyKeys(input)
  if (!input || typeof input !== 'object') {
    throw new Error('Input must be an object.')
  }
  const rec = input as Record<string, unknown>
  const workspaceId = typeof rec.workspaceId === 'string' ? rec.workspaceId.trim() : ''
  if (!workspaceId) throw new Error('workspaceId is required.')

  const partyId = typeof rec.partyId === 'string' && rec.partyId.trim() ? rec.partyId.trim() : undefined
  const name = validateConPartyName(rec.name)
  const type = validateConPartyType(rec.type)
  const trade = validateConPartyTrade(rec.trade)
  const externalRef = validateConPartyExternalRef(rec.externalRef)
  const projectIds = validateConPartyProjectIds(rec.projectIds)
  const archived = rec.archived !== undefined ? Boolean(rec.archived) : undefined
  const opId = typeof rec.opId === 'string' && rec.opId.trim() ? rec.opId.trim() : undefined

  return {
    workspaceId,
    partyId,
    name,
    type,
    trade,
    externalRef,
    projectIds,
    archived,
    opId,
  }
}

export function validateListConPartiesInput(input: unknown): ListConPartiesInput {
  assertNoMoneyKeys(input)
  if (!input || typeof input !== 'object') {
    throw new Error('Input must be an object.')
  }
  const rec = input as Record<string, unknown>
  const workspaceId = typeof rec.workspaceId === 'string' ? rec.workspaceId.trim() : ''
  if (!workspaceId) throw new Error('workspaceId is required.')

  const projectId = typeof rec.projectId === 'string' && rec.projectId.trim() ? rec.projectId.trim() : undefined
  const includeArchived = rec.includeArchived !== undefined ? Boolean(rec.includeArchived) : undefined
  let limit: number | undefined
  if (typeof rec.limit === 'number' && Number.isFinite(rec.limit)) {
    limit = Math.max(1, Math.min(200, Math.floor(rec.limit)))
  }

  return {
    workspaceId,
    projectId,
    includeArchived,
    limit,
  }
}

export function validateSaveConMeasurementInput(input: unknown): SaveConMeasurementInput {
  assertNoMoneyKeys(input)
  if (!input || typeof input !== 'object') {
    throw new Error('Input must be an object.')
  }
  const rec = input as Record<string, unknown>
  const workspaceId = typeof rec.workspaceId === 'string' ? rec.workspaceId.trim() : ''
  if (!workspaceId) throw new Error('workspaceId is required.')

  const projectId = typeof rec.projectId === 'string' ? rec.projectId.trim() : ''
  if (!projectId) throw new Error('projectId is required.')

  const partyId = typeof rec.partyId === 'string' ? rec.partyId.trim() : ''
  if (!partyId) throw new Error('partyId is required.')

  const placeId = typeof rec.placeId === 'string' ? rec.placeId.trim() : ''
  if (!placeId) throw new Error('placeId is required.')

  const measurementId = typeof rec.measurementId === 'string' && rec.measurementId.trim() ? rec.measurementId.trim() : undefined
  const workOrderRef = validateWorkOrderRef(rec.workOrderRef)
  const itemRef = validateItemRef(rec.itemRef)
  const itemDescription = validateItemDescription(rec.itemDescription)
  const unit = validateMeasurementUnit(rec.unit)
  const measuredDay = validateMeasuredDay(rec.measuredDay)
  const periodLabel = validatePeriodLabel(rec.periodLabel)
  const measuredQty = validateMeasurementQuantity(rec.measuredQty, 'measuredQty')

  const photoPaths: string[] = []
  if (Array.isArray(rec.photoPaths)) {
    for (const p of rec.photoPaths) {
      if (typeof p === 'string' && p.trim()) {
        photoPaths.push(p.trim())
      }
    }
  }

  const inspectionRefs = validateInspectionRefs(rec.inspectionRefs)
  const sourceLinks = validateSourceLinks(rec.sourceLinks)
  const opId = typeof rec.opId === 'string' && rec.opId.trim() ? rec.opId.trim() : undefined

  return {
    workspaceId,
    projectId,
    measurementId,
    partyId,
    placeId,
    workOrderRef,
    itemRef,
    itemDescription,
    unit,
    measuredDay,
    periodLabel,
    measuredQty,
    photoPaths,
    inspectionRefs,
    sourceLinks,
    opId,
  }
}

export function validateSubmitConMeasurementInput(input: unknown): SubmitConMeasurementInput {
  assertNoMoneyKeys(input)
  if (!input || typeof input !== 'object') {
    throw new Error('Input must be an object.')
  }
  const rec = input as Record<string, unknown>
  const workspaceId = typeof rec.workspaceId === 'string' ? rec.workspaceId.trim() : ''
  if (!workspaceId) throw new Error('workspaceId is required.')

  const projectId = typeof rec.projectId === 'string' ? rec.projectId.trim() : ''
  if (!projectId) throw new Error('projectId is required.')

  const measurementId = typeof rec.measurementId === 'string' ? rec.measurementId.trim() : ''
  if (!measurementId) throw new Error('measurementId is required.')

  const opId = typeof rec.opId === 'string' && rec.opId.trim() ? rec.opId.trim() : undefined

  return {
    workspaceId,
    projectId,
    measurementId,
    opId,
  }
}

export function validateAcceptConMeasurementInput(input: unknown): AcceptConMeasurementInput {
  assertNoMoneyKeys(input)
  if (!input || typeof input !== 'object') {
    throw new Error('Input must be an object.')
  }
  const rec = input as Record<string, unknown>
  const workspaceId = typeof rec.workspaceId === 'string' ? rec.workspaceId.trim() : ''
  if (!workspaceId) throw new Error('workspaceId is required.')

  const projectId = typeof rec.projectId === 'string' ? rec.projectId.trim() : ''
  if (!projectId) throw new Error('projectId is required.')

  const measurementId = typeof rec.measurementId === 'string' ? rec.measurementId.trim() : ''
  if (!measurementId) throw new Error('measurementId is required.')

  const acceptedQty = validateMeasurementQuantity(rec.acceptedQty, 'acceptedQty')
  const note = rec.note !== undefined && rec.note !== null ? String(rec.note).trim() : undefined
  const selfAcceptReason =
    typeof rec.selfAcceptReason === 'string' && rec.selfAcceptReason.trim()
      ? rec.selfAcceptReason.trim()
      : undefined
  const opId = typeof rec.opId === 'string' && rec.opId.trim() ? rec.opId.trim() : undefined

  return {
    workspaceId,
    projectId,
    measurementId,
    acceptedQty,
    note: note || undefined,
    selfAcceptReason,
    opId,
  }
}

export function validateDisputeConMeasurementInput(input: unknown): DisputeConMeasurementInput {
  assertNoMoneyKeys(input)
  if (!input || typeof input !== 'object') {
    throw new Error('Input must be an object.')
  }
  const rec = input as Record<string, unknown>
  const workspaceId = typeof rec.workspaceId === 'string' ? rec.workspaceId.trim() : ''
  if (!workspaceId) throw new Error('workspaceId is required.')

  const projectId = typeof rec.projectId === 'string' ? rec.projectId.trim() : ''
  if (!projectId) throw new Error('projectId is required.')

  const measurementId = typeof rec.measurementId === 'string' ? rec.measurementId.trim() : ''
  if (!measurementId) throw new Error('measurementId is required.')

  const reason = validateDisputeReason(rec.reason)
  const selfAcceptReason =
    typeof rec.selfAcceptReason === 'string' && rec.selfAcceptReason.trim()
      ? rec.selfAcceptReason.trim()
      : undefined
  const opId = typeof rec.opId === 'string' && rec.opId.trim() ? rec.opId.trim() : undefined

  return {
    workspaceId,
    projectId,
    measurementId,
    reason,
    selfAcceptReason,
    opId,
  }
}

export function validateCorrectConMeasurementInput(input: unknown): CorrectConMeasurementInput {
  assertNoMoneyKeys(input)
  if (!input || typeof input !== 'object') {
    throw new Error('Input must be an object.')
  }
  const rec = input as Record<string, unknown>
  const workspaceId = typeof rec.workspaceId === 'string' ? rec.workspaceId.trim() : ''
  if (!workspaceId) throw new Error('workspaceId is required.')

  const projectId = typeof rec.projectId === 'string' ? rec.projectId.trim() : ''
  if (!projectId) throw new Error('projectId is required.')

  const measurementId = typeof rec.measurementId === 'string' ? rec.measurementId.trim() : ''
  if (!measurementId) throw new Error('measurementId is required.')

  const newAcceptedQty = validateMeasurementQuantity(rec.newAcceptedQty, 'newAcceptedQty')
  const reason = validateCorrectionReason(rec.reason)
  const selfAcceptReason =
    typeof rec.selfAcceptReason === 'string' && rec.selfAcceptReason.trim()
      ? rec.selfAcceptReason.trim()
      : undefined
  const opId = typeof rec.opId === 'string' && rec.opId.trim() ? rec.opId.trim() : undefined

  return {
    workspaceId,
    projectId,
    measurementId,
    newAcceptedQty,
    reason,
    selfAcceptReason,
    opId,
  }
}

export function validateCancelConMeasurementInput(input: unknown): CancelConMeasurementInput {
  assertNoMoneyKeys(input)
  if (!input || typeof input !== 'object') {
    throw new Error('Input must be an object.')
  }
  const rec = input as Record<string, unknown>
  const workspaceId = typeof rec.workspaceId === 'string' ? rec.workspaceId.trim() : ''
  if (!workspaceId) throw new Error('workspaceId is required.')

  const projectId = typeof rec.projectId === 'string' ? rec.projectId.trim() : ''
  if (!projectId) throw new Error('projectId is required.')

  const measurementId = typeof rec.measurementId === 'string' ? rec.measurementId.trim() : ''
  if (!measurementId) throw new Error('measurementId is required.')

  const reason = validateCancellationReason(rec.reason)
  const opId = typeof rec.opId === 'string' && rec.opId.trim() ? rec.opId.trim() : undefined

  return {
    workspaceId,
    projectId,
    measurementId,
    reason,
    ...(opId ? { opId } : {}),
  }
}

export function validateGetConMeasurementInput(input: unknown): GetConMeasurementInput {
  assertNoMoneyKeys(input)
  if (!input || typeof input !== 'object') {
    throw new Error('Input must be an object.')
  }
  const rec = input as Record<string, unknown>
  const workspaceId = typeof rec.workspaceId === 'string' ? rec.workspaceId.trim() : ''
  if (!workspaceId) throw new Error('workspaceId is required.')

  const projectId = typeof rec.projectId === 'string' ? rec.projectId.trim() : ''
  if (!projectId) throw new Error('projectId is required.')

  const measurementId = typeof rec.measurementId === 'string' ? rec.measurementId.trim() : ''
  if (!measurementId) throw new Error('measurementId is required.')

  return { workspaceId, projectId, measurementId }
}

export function validateListConMeasurementsInput(input: unknown): ListConMeasurementsInput {
  assertNoMoneyKeys(input)
  if (!input || typeof input !== 'object') {
    throw new Error('Input must be an object.')
  }
  const rec = input as Record<string, unknown>
  const workspaceId = typeof rec.workspaceId === 'string' ? rec.workspaceId.trim() : ''
  if (!workspaceId) throw new Error('workspaceId is required.')

  const projectId = typeof rec.projectId === 'string' ? rec.projectId.trim() : ''
  if (!projectId) throw new Error('projectId is required.')

  const partyId = typeof rec.partyId === 'string' && rec.partyId.trim() ? rec.partyId.trim() : undefined

  let state: ConMeasurementState | undefined
  if (typeof rec.state === 'string' && rec.state.trim()) {
    if (!CON_MEASUREMENT_STATES.includes(rec.state as ConMeasurementState)) {
      throw new Error(`Invalid state: ${rec.state}`)
    }
    state = rec.state as ConMeasurementState
  }

  const fromDay = typeof rec.fromDay === 'string' && rec.fromDay.trim() ? validateMeasuredDay(rec.fromDay) : undefined
  const toDay = typeof rec.toDay === 'string' && rec.toDay.trim() ? validateMeasuredDay(rec.toDay) : undefined

  let limit: number | undefined
  if (typeof rec.limit === 'number' && Number.isFinite(rec.limit)) {
    limit = Math.max(1, Math.min(200, Math.floor(rec.limit)))
  }

  return {
    workspaceId,
    projectId,
    partyId,
    state,
    fromDay,
    toDay,
    limit,
  }
}

export function validateListDisputedConMeasurementsInput(input: unknown): ListDisputedConMeasurementsInput {
  assertNoMoneyKeys(input)
  if (!input || typeof input !== 'object') {
    throw new Error('Input must be an object.')
  }
  const rec = input as Record<string, unknown>
  const workspaceId = typeof rec.workspaceId === 'string' ? rec.workspaceId.trim() : ''
  if (!workspaceId) throw new Error('workspaceId is required.')

  const projectId = typeof rec.projectId === 'string' ? rec.projectId.trim() : ''
  if (!projectId) throw new Error('projectId is required.')

  let limit: number | undefined
  if (typeof rec.limit === 'number' && Number.isFinite(rec.limit)) {
    limit = Math.max(1, Math.min(200, Math.floor(rec.limit)))
  }

  return { workspaceId, projectId, limit }
}

export function validateGetConMeasureTotalsInput(input: unknown): GetConMeasureTotalsInput {
  assertNoMoneyKeys(input)
  if (!input || typeof input !== 'object') {
    throw new Error('Input must be an object.')
  }
  const rec = input as Record<string, unknown>
  const workspaceId = typeof rec.workspaceId === 'string' ? rec.workspaceId.trim() : ''
  if (!workspaceId) throw new Error('workspaceId is required.')

  const projectId = typeof rec.projectId === 'string' ? rec.projectId.trim() : ''
  if (!projectId) throw new Error('projectId is required.')

  const partyId = typeof rec.partyId === 'string' && rec.partyId.trim() ? rec.partyId.trim() : undefined
  const workOrderRef = typeof rec.workOrderRef === 'string' && rec.workOrderRef.trim() ? rec.workOrderRef.trim() : undefined

  let limit: number | undefined
  if (typeof rec.limit === 'number' && Number.isFinite(rec.limit)) {
    limit = Math.max(1, Math.min(200, Math.floor(rec.limit)))
  }

  return {
    workspaceId,
    projectId,
    partyId,
    workOrderRef,
    limit,
  }
}
